import type { AuditLogEntryDto } from '@/services/types';

import type {
    AuditLogEntry,
    AuditLogFacets,
    AuditScopeOption,
    AuditScopeRef,
    AuditScopeState,
} from './types';
import { AUDIT_OUTCOMES, AUDIT_SCOPE_KINDS, AUDIT_SCOPE_STATES } from './types';

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function jsonObjectOrNull(value: unknown): Record<string, unknown> | null {
    return isRecord(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

/**
 * Only a row that was found can carry a name, so a state the page does not know yet
 * reads as active when a name came with it and as missing when none did.
 */
function scopeState(state: unknown, name: string | null): AuditScopeState {
    return (
        AUDIT_SCOPE_STATES.find((known) => known === state) ??
        (name === null ? 'missing' : 'active')
    );
}

function scopeRef(id: number | undefined, name: unknown, state: unknown): AuditScopeRef | null {
    if (typeof id !== 'number') return null;
    const currentName = stringOrNull(name);
    return { id, name: currentName, state: scopeState(state, currentName) };
}

export function mapAuditLogEntry(dto: AuditLogEntryDto): AuditLogEntry {
    return {
        id: dto.id,
        occurredAt: new Date(dto.occurredAt),
        actorType: dto.actorType,
        actorUserId: dto.actorUserId ?? null,
        actorEmail: dto.actorEmail ?? null,
        impersonatorUserId: dto.impersonatorUserId ?? null,
        impersonatorEmail: dto.impersonatorEmail ?? null,
        action: dto.action,
        // The backend's CHECK constraint allows only the three known outcomes, but an
        // unrecognised value must not blow up the audit viewer, so it reads as 'failed'.
        outcome: AUDIT_OUTCOMES.find((outcome) => outcome === dto.outcome) ?? 'failed',
        statusCode: dto.statusCode ?? null,
        entityType: dto.entityType,
        entityId: dto.entityId ?? null,
        entityLabel: dto.entityLabel ?? null,
        client: scopeRef(dto.clientId ?? undefined, dto.currentClientName, dto.currentClientState),
        location: scopeRef(
            dto.locationId ?? undefined,
            dto.currentLocationName,
            dto.currentLocationState
        ),
        cage: scopeRef(dto.cageId ?? undefined, dto.currentCageName, dto.currentCageState),
        requestId: dto.requestId ?? null,
        ip: dto.ip ?? null,
        userAgent: dto.userAgent ?? null,
        method: dto.method ?? null,
        route: dto.route ?? null,
        changes: jsonObjectOrNull(dto.changes),
        metadata: jsonObjectOrNull(dto.metadata),
    };
}

function strings(value: unknown): string[] {
    return Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : [];
}

/** Response bodies are read as `unknown`, since nothing checks them against the spec. */
export function mapAuditLogFacets(body: unknown): AuditLogFacets {
    if (!isRecord(body)) return { actions: [], entityTypes: [] };
    return { actions: strings(body.actions), entityTypes: strings(body.entityTypes) };
}

function mapAuditScopeOption(item: unknown): AuditScopeOption | null {
    if (!isRecord(item)) return null;
    const kind = AUDIT_SCOPE_KINDS.find((known) => known === item.kind);
    if (kind === undefined || typeof item.id !== 'number') return null;
    const name = stringOrNull(item.name);
    return {
        kind,
        id: item.id,
        name,
        state: scopeState(item.state, name),
        clientName: stringOrNull(item.clientName),
        locationName: stringOrNull(item.locationName),
    };
}

/** A kind the page does not know cannot be filtered on, so it is left out. */
export function mapAuditScopeOptions(body: unknown): AuditScopeOption[] {
    if (!Array.isArray(body)) return [];
    return body
        .map(mapAuditScopeOption)
        .filter((option): option is AuditScopeOption => option !== null);
}

function parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

function nextLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    // The Date constructor rolls day + 1 into the next month/year on its own,
    // so this stays correct across month and year boundaries.
    return new Date(year, month - 1, day + 1);
}

export function dateRangeToFilterBounds(
    dateFrom?: string,
    dateTo?: string
): { from?: Date; to?: Date } {
    const bounds: { from?: Date; to?: Date } = {};
    if (dateFrom) bounds.from = parseLocalDate(dateFrom);
    // `to` is the exclusive upper bound: the picker's end date is inclusive, so the
    // filter needs local midnight of the following day.
    if (dateTo) bounds.to = nextLocalDate(dateTo);
    return bounds;
}
